import { useState, useEffect } from "react";
import { Users, Plus, Edit2, Trash2, Phone, Mail, Shield, Truck, Search, X, Eye, EyeOff, Store, ChefHat, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AdminLayout } from "@/components/layouts/AdminLayout";
import { toast } from "sonner";
import axios from "axios";
import API from "@/utils/api";

const ROLE_CONFIG = {
  driver: { label: "Repartidor", icon: Truck, color: "bg-blue-100 text-blue-800" },
  admin: { label: "Administrador", icon: Shield, color: "bg-purple-100 text-purple-800" },
  cashier: { label: "Cajero", icon: Store, color: "bg-green-100 text-green-800" },
  kitchen: { label: "Cocina", icon: ChefHat, color: "bg-orange-100 text-orange-800" },
  screen: { label: "Pantalla Pedidos", icon: Eye, color: "bg-yellow-100 text-yellow-800" },
  customer: { label: "Cliente", icon: Users, color: "bg-gray-100 text-gray-800" },
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState("all");

  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role: "driver",
    branch_id: "",
  });

  const fetchUsers = async () => {
    try {
      const [usersRes, branchesRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/branches`, { withCredentials: true })
      ]);
      // Filter out customers, only show drivers and admins
      const staffUsers = usersRes.data.filter(u => u.role !== "customer");
      setUsers(staffUsers);
      setFilteredUsers(staffUsers);
      setBranches(branchesRes.data.filter(b => b.active));
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    let result = users;

    if (searchQuery) {
      result = result.filter(u =>
        u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.phone?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (filterRole !== "all") {
      result = result.filter(u => u.role === filterRole);
    }

    setFilteredUsers(result);
  }, [searchQuery, filterRole, users]);

  const handleCreate = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Nombre, email y contraseña son obligatorios");
      return;
    }

    try {
      await axios.post(`${API}/admin/users`, formData, { withCredentials: true });
      toast.success("Usuario creado correctamente");
      setShowCreateDialog(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear usuario");
    }
  };

  const handleUpdate = async () => {
    if (!selectedUser) return;

    try {
      const updateData = {
        name: formData.name,
        phone: formData.phone,
        active: selectedUser.active,
        role: formData.role,
        assigned_branches: formData.branch_id ? [formData.branch_id] : []
      };

      if (formData.password) {
        updateData.password = formData.password;
      }

      await axios.put(`${API}/admin/users/${selectedUser.user_id}`, updateData, { withCredentials: true });
      toast.success("Usuario actualizado correctamente");
      setShowEditDialog(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar usuario");
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    try {
      await axios.delete(`${API}/admin/users/${selectedUser.user_id}`, { withCredentials: true });
      toast.success("Usuario eliminado correctamente");
      setShowDeleteDialog(false);
      setSelectedUser(null);
      setDeletePassword("");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar usuario");
    }
  };

  const handleToggleActive = async (user) => {
    try {
      const newStatus = !user.active;
      await axios.put(`${API}/admin/users/${user.user_id}`, {
        name: user.name,
        phone: user.phone,
        active: newStatus
      }, { withCredentials: true });

      toast.success(newStatus ? "Usuario activado correctamente" : "Usuario desactivado correctamente");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar estado del usuario");
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone || "",
      password: "",
      role: user.role,
      branch_id: user.assigned_branches?.[0] || "",
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      password: "",
      role: "driver",
      branch_id: "",
    });
    setSelectedUser(null);
    setShowPassword(false);
  };

  if (isLoading) {
    return (
      <AdminLayout title="Usuarios">
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-800 rounded-xl animate-pulse" />
          ))}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Usuarios">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-white">Gestión de Personal</h1>
            <p className="text-gray-400">
              {filteredUsers.length} {filteredUsers.length === 1 ? "usuario" : "usuarios"}
            </p>
          </div>
          <Button
            onClick={() => { resetForm(); setShowCreateDialog(true); }}
            className="bg-yellow-500 hover:bg-yellow-400 text-black"
            data-testid="create-user-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Usuario
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Buscar por nombre, email o teléfono..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-gray-900 border-gray-700 text-white"
              data-testid="search-users"
            />
          </div>
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="w-full sm:w-48 bg-gray-900 border-gray-700 text-white">
              <SelectValue placeholder="Filtrar por rol" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los roles</SelectItem>
              <SelectItem value="driver">Repartidores</SelectItem>
              <SelectItem value="admin">Administradores</SelectItem>
              <SelectItem value="cashier">Cajeros</SelectItem>
              <SelectItem value="kitchen">Cocina</SelectItem>
              <SelectItem value="screen">Pantallas TV</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Users Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUsers.map((user) => {
            const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.customer;
            const RoleIcon = roleConfig.icon;

            return (
              <Card
                key={user.user_id}
                className={`bg-gray-900 border-gray-800 ${!user.active ? 'opacity-50' : ''}`}
                data-testid={`user-card-${user.user_id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gray-800 flex items-center justify-center overflow-hidden border border-gray-700">
                        {user.picture ? (
                          <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                        ) : (
                          <RoleIcon className="w-6 h-6 text-primary" />
                        )}
                      </div>
                      <div>
                        <CardTitle className="text-lg text-white">{user.name}</CardTitle>
                        <Badge className={`${roleConfig.color} border-none font-bold`}>{roleConfig.label}</Badge>
                      </div>
                    </div>
                    {!user.active && (
                      <Badge variant="destructive">Inactivo</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-gray-400">
                    <Mail className="w-4 h-4" />
                    <span className="text-sm truncate">{user.email}</span>
                  </div>
                  {user.phone && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <Phone className="w-4 h-4" />
                      <span className="text-sm">{user.phone}</span>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 border-gray-700 text-white hover:bg-gray-800"
                      onClick={() => openEditDialog(user)}
                      data-testid={`edit-user-${user.user_id}`}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className={user.active
                        ? "border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black"
                        : "border-green-500 text-green-500 hover:bg-green-500 hover:text-white"}
                      onClick={() => handleToggleActive(user)}
                      title={user.active ? "Desactivar usuario" : "Activar usuario"}
                    >
                      {user.active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                      onClick={() => openDeleteDialog(user)}
                      data-testid={`delete-user-${user.user_id}`}
                      title="Eliminar usuario permanentemente"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12 bg-gray-900 rounded-xl border border-gray-800">
            <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No se encontraron usuarios</p>
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle>Nuevo Usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Nombre *</label>
              <Input
                placeholder="Nombre completo"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-gray-800 border-gray-700"
                data-testid="input-name"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Email *</label>
              <Input
                type="email"
                placeholder="correo@ejemplo.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-gray-800 border-gray-700"
                data-testid="input-email"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Teléfono</label>
              <Input
                placeholder="+34 612 345 678"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="bg-gray-800 border-gray-700"
                data-testid="input-phone"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Contraseña *</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Mínimo 6 caracteres"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="bg-gray-800 border-gray-700 pr-10"
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Rol *</label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                <SelectTrigger className="bg-gray-800 border-gray-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="driver">Repartidor</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="cashier">Cajero</SelectItem>
                  <SelectItem value="kitchen">Cocina</SelectItem>
                  <SelectItem value="screen">Pantalla Pedidos</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {formData.role === "cashier" && "Solo puede acceder al TPV/Caja"}
                {formData.role === "kitchen" && "Solo puede acceder a la pantalla de Cocina"}
                {formData.role === "screen" && "Específico para la TV de pedidos listos"}
                {formData.role === "driver" && "Puede gestionar entregas"}
                {formData.role === "admin" && "Acceso completo al sistema"}
              </p>
            </div>
            {formData.role === "driver" && (
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Sucursal Asignada *</label>
                <Select value={formData.branch_id} onValueChange={(v) => setFormData({ ...formData, branch_id: v })}>
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue placeholder="Selecciona una sucursal" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map(branch => (
                      <SelectItem key={branch.branch_id} value={branch.branch_id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-gray-700">
              Cancelar
            </Button>
            <Button onClick={handleCreate} className="bg-yellow-500 hover:bg-yellow-400 text-black" data-testid="submit-create">
              Crear Usuario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Nombre</label>
              <Input
                placeholder="Nombre completo"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-gray-800 border-gray-700"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Email (no editable)</label>
              <Input
                value={formData.email}
                disabled
                className="bg-gray-800 border-gray-700 opacity-50"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Teléfono</label>
              <Input
                placeholder="+34 612 345 678"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="bg-gray-800 border-gray-700"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Nueva Contraseña (dejar vacío para no cambiar)</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Nueva contraseña"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="bg-gray-800 border-gray-700 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Rol</label>
              <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                <SelectTrigger className="bg-gray-800 border-gray-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="driver">Repartidor</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="cashier">Cajero</SelectItem>
                  <SelectItem value="kitchen">Cocina</SelectItem>
                  <SelectItem value="screen">Pantalla Pedidos</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {formData.role === "cashier" && "Solo puede acceder al TPV/Caja"}
                {formData.role === "kitchen" && "Solo puede acceder a la pantalla de Cocina"}
                {formData.role === "screen" && "Específico para la TV de pedidos listos"}
                {formData.role === "driver" && "Puede gestionar entregas"}
                {formData.role === "admin" && "Acceso completo al sistema"}
              </p>
            </div>
            {formData.role === "driver" && (
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Sucursal Asignada</label>
                <Select value={formData.branch_id} onValueChange={(v) => setFormData({ ...formData, branch_id: v })}>
                  <SelectTrigger className="bg-gray-800 border-gray-700">
                    <SelectValue placeholder="Selecciona una sucursal" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map(branch => (
                      <SelectItem key={branch.branch_id} value={branch.branch_id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="border-gray-700">
              Cancelar
            </Button>
            <Button onClick={handleUpdate} className="bg-yellow-500 hover:bg-yellow-400 text-black" data-testid="submit-edit">
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(open) => { setShowDeleteDialog(open); if (!open) setDeletePassword(""); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle>Eliminar Usuario Permanentemente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-400">
              ¿Estás seguro de que deseas <strong className="text-red-500">eliminar permanentemente</strong> a <strong className="text-white">{selectedUser?.name}</strong>?
            </p>
            <p className="text-red-400 text-sm">
              ⚠️ Esta acción no se puede deshacer. El usuario y todos sus datos serán eliminados completamente.
            </p>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">
                Confirma tu contraseña de administrador *
              </label>
              <div className="relative">
                <Input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Ingresa tu contraseña"
                  className="bg-gray-800 border-gray-700 text-white pr-10"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeleteDialog(false); setDeletePassword(""); }} className="border-gray-700">
              Cancelar
            </Button>
            <Button onClick={handleDelete} variant="destructive" data-testid="confirm-delete">
              Eliminar Permanentemente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout >
  );
}
